// ** React imports
import { Fragment, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Avatar,
  StepLabel,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  IconButton,
  InputAdornment,
  Radio,
  FormControlLabel,
  ListItem,
  FormHelperText,
  Divider,
  List,
  MenuItem,
  Checkbox,
} from "@mui/material";
import MuiStep from "@mui/material/Step";
import { styled, useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Icon Imports
import Icon from "components/icon";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// ** Hook Import
import { hexToRGBA } from "components/utils/hex-to-rgba";

// ** Custom Components Imports
import StepperCustomDot from "components/mui/StepperCustomDot";
import CustomAvatar from "components/mui/avatar";
import CustomChip from "components/mui/chip";
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";

// ** Third Party Components
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import PhoneInput from "react-phone-input-2";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import _ from "lodash";
import dayjs from "dayjs";

import AddIcon from "@mui/icons-material/Add";
// ** Logic Imports
import { stateNames , expandStateName} from "components/US_StatesNames";

// ** API Imports
import { GetEmployees } from "../logic/GetEmployees";
import { getDepartments } from "../logic/Departmentlogic";
import { getJobTitles } from "./JobTitlelogic";
import { SaveEmployee } from "../logic/SaveEmployee";
import { GetSkills } from "../logic/GetSkills";
import { GetRoles } from "screens/Settings/logic/GetRoles";
import { getCounties } from "components/USTimezone_county";
import { useIndexLogic } from "screens/Settings/logic/index-logic";
import AttachmentsSectionEmployee from 'screens/Employees/component/AttachmentsSectionEmployee'

const FileUploader = () => {
  // ** State
  const [files, setFiles] = useState([]);

  // ** Hooks
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <Icon icon="tabler:file-description" />;
    }
  };

  const handleRemoveFile = (file) => {
    const uploadedFiles = files;
    const filtered = uploadedFiles.filter((i) => i.name !== file.name);
    setFiles([...filtered]);
  };

  const fileList = files.map((file) => (
    <ListItem key={file.name}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          justifyContent: "space-between",
          border: "1px solid #E1E4EA",
          p: 2,
          borderRadius: 2,
        }}
      >
        <Box>{renderFilePreview(file)}</Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography className="file-name">{file.name}</Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </Box>
        <Box>
          {/* <IconButton>
            <Icon icon="tabler:eye" fontSize={20} />
          </IconButton> */}
          <IconButton onClick={() => handleRemoveFile(file)}>
            <Icon icon="tabler:trash"  fontSize={20} />
          </IconButton>
        </Box>
      </Box>
    </ListItem>
  ));

  const handleRemoveAllFiles = () => {
    setFiles([]);
  };

  return (
    <Fragment>
      <div
        {...getRootProps({ className: "dropzone" })}
        style={{ width: "100%" }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#F5F7FA",
            p: 4,
            width: "100%",
            cursor: "pointer",
            borderRadius: 1,
          }}
        >
          <CloudUploadIcon sx={{ color: "#454F64" }} />
          <Typography sx={{ color: "#454F64", ml: 4 }}>
            Drop files to attach or click here to browse
          </Typography>
        </Box>
      </div>
      {files.length ? (
        <Box>
          <Box>
            <List>{fileList}</List>
          </Box>
          <Button
            color="error"
            size="small"
            sx={{ ml: 4}}
            variant="outlined"
            onClick={handleRemoveAllFiles}
          >
            Remove All
          </Button>
        </Box>
      ) : null}
    </Fragment>
  );
};

const steps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:align-box-left-stretch",
    title: "Employee Details",
  },
  // {
  //   icon: "tabler:paperclip",
  //   title: "Attachments",
  // },
];

const CustomStep = styled(MuiStep)(({ theme }) => ({
  "& .MuiStepLabel-root": {
    paddingTop: 0,
  },
  "& .MuiStepLabel-iconContainer": {
    display: "none",
  },
  "& .step-subtitle": {
    color: `${theme.palette.text.disabled} !important`,
  },
  "& + svg": {
    color: theme.palette.text.disabled,
  },
  "&.Mui-completed .step-title": {
    color: theme.palette.text.disabled,
  },
}));

export const useAddEmployeeLogic = ({handleClose, fetchEmployees}) => {
  //#region Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  //#endregion Hooks

  //#region Constants
  const phoneFields = [{ phoneNo: "", isPrimary: false }];
  const WeekDay = [
    { dayOfWeek: "Monday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Tuesday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Wednesday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Thursday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Friday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Saturday", hoursFrom: null, hoursTo: null },
    { dayOfWeek: "Sunday", hoursFrom: null, hoursTo: null },
  ];
  const AlwaysAvailableWeekDay = [
    { dayOfWeek: "Monday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Tuesday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Wednesday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Thursday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Friday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Saturday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
    { dayOfWeek: "Sunday", hoursFrom: "07:00:00", hoursTo: "21:00:00" },
  ];
  const initialdata = {
    address: null,
    city: null,
    clockInOutRequired: false,
    companyID: 0,
    county: null,
    createBy: null,
    createdBy: null,
    createDate: null,
    dateOfBirth: null,
    departmentID: null,
    drivingLicenseExpiryDate: null,
    drivingLicenseValid: false,
    email: null,
    emergencyContactName: null,
    emergencyContactPhone: null,
    emergencyContactRelationship: null,
    employeeID: 0,
    employmentStatus: null,
    firstName: null,
    hireDate: null,
    isActive: true,
    jobTitle: null,
    lastName: null,
    modifiedBy: null,
    modifiedDate: null,
    phoneNumber: null,
    roleID: null,
    skillID: null,
    postalCode: null,
    probationEndDate: null,
    referredBy: null,
    stateProvince: null,
    title: null,
    userID: null,
    wageAmount: null,
    wageType: null,
    empType: "Employees",
    weeklyHours: 40,
    overtimeExempt: null,
    phoneNumbers: phoneFields,
    tags: null,
    employeeHours: WeekDay,
    street2: null,
  };
  const mandatoryFields = [
    { key: "address", displayName: "Street 1" },
    { key: "city", displayName: "City" },
    { key: "stateProvince", displayName: "State" },
    { key: "postalCode", displayName: "Zip Code"},
    { key: "departmentID", displayName: "Department" },
    { key: "email", displayName: "Email" },
    { key: "employmentStatus", displayName: "Employment Status" },
    { key: "firstName", displayName: "First Name" },
    { key: "jobTitle", displayName: "Job Title" },
    { key: "lastName", displayName: "Last Name" },
    { key: "roleID", displayName: "Employee Role" },
    { key: "skillID", displayName: "Skill Type" },
    { key: "wageType", displayName: "Wage Type" },
    { key: "empType", displayName: "Employee Type" },
    { key: "wageAmount", displayName: "Wage Amount" },
  ];
  //#endregion Constants

  //#region States
  const [activeStep, setActiveStep] = useState(0);
  const [value, setValue] = useState("1");
  const [btnLoading, setButtonLoading] = useState(false);
  const [phoneError, setPhoneError] = useState({});
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState({ 0: true });
  const [AllEmployees, setAllEmployees] = useState();
  const [employee, setEmployee] = useState(initialdata);
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState({});
  const [isAlwaysAvailable, setIsAlwaysAvailable] = useState(false);
  const [isValidEmergencyPhone, setIsValidEmergencyPhone] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [jobTitleOpen, setJobTitleOpen] = useState(false);
  const [skillTypeOpen, setSkillTypeOpen] = useState(false);
  const [skills, setSkills] = useState([]);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
const [jobTitles, setJobTitles] = useState([]);
const [jobTitleDialogOpen, setJobTitleDialogOpen] = useState(false);


  // const [customRoles, setCustomRoles] = useState([]);


  const { SkillProps, RoleProps } = useIndexLogic(toast);

  const { skillsList, handleOpenSkillTypeDlg } = SkillProps;
  const { handleOpenRoleDlg } = RoleProps

  const resetForm = () => {
  setEmployee(initialdata);
  setErrors({});
  setActiveStep(0);
  setPhoneError({});
  setIsValidPhone({ 0: true });
};


  //#endregion Status

  const customRoles = RoleProps.roles;

  //#region Handlers
  useEffect(() => {
    fetchData(0);
    fetchDepartments();
    fetchJobTitles();
    fetchSkills();
    RoleProps.fetchRoles(0);
  }, []);
  
  // const fetchRoles = async () => {
  //   try {
  //     const roles = await GetRoles(0);
  //     setCustomRoles(roles);
  //   } catch (error) {
  //     console.error('Failed to fetch roles: ', error)
  //   }
  // }

  useEffect(() => {
    const AlwaysAvailable = (employee?.employeeHours || WeekDay).every(
      (day) => day.hoursFrom && day.hoursTo
    );
    setIsAlwaysAvailable(AlwaysAvailable);
  }, [employee]);

  useEffect(() => {
    if (!isAlwaysAvailable && getWorkingHours(employee)?.hoursFrom) {
      setErrors((prevState) => ({
        ...prevState,
        ["hoursFrom"]: "",
        ["hoursTo"]: "",
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        ["hoursFrom"]: "Set availabilty first.",
        ["hoursTo"]: "Set availabilty first.",
      }));
    }
    if (isAlwaysAvailable) {
      setErrors((prevState) => ({
        ...prevState,
        ["hoursFrom"]: "",
        ["hoursTo"]: "",
      }));
    }
  }, [isAlwaysAvailable, employee?.employeeHours]);
  useEffect(() => {
  if (Object.keys(errors).length > 0) {
    const timer = setTimeout(() => {
      setErrors({});
    }, 5000);

    return () => clearTimeout(timer);
  }
}, [errors]);


  const validateFields = (step) => {
    let tempErrors = {};
    const nameRegex = /^[A-Za-z]+$/;

    PhoneResolve();
    if (step === 0) {
      if (employee.firstName) {
        if (!nameRegex.test(employee.firstName)) {
          tempErrors.firstName = "Only alphabets are accepted";
          tempErrors.step = "1";
        }
      }

      if (!employee.firstName) {
        tempErrors.firstName = "First Name is required";
        tempErrors.step = "1";
      }

      if (employee.lastName) {
        if (!nameRegex.test(employee.lastName)) {
          tempErrors.lastName = "Only alphabets are accepted";
          tempErrors.step = "1";
        }
      }

      if (!employee.lastName) {
        tempErrors.lastName = "Last Name is required";
        tempErrors.step = "1";
      }

      if (!employee.email) {
        tempErrors.email = "Email is required";
        tempErrors.step = "1";
      } else if (!validateEmail(employee.email)) {
        tempErrors.email = "Enter a valid Email";
        tempErrors.step = "1";
      }
        else {
      // Check for duplicate email
      const isDuplicateEmail = AllEmployees?.some(
        emp => emp.email?.toLowerCase() === employee.email?.toLowerCase()
      );

      if (isDuplicateEmail) {
        tempErrors.email = "This email already exists";
        tempErrors.step = "1";
      }
    }

      if (!employee.jobTitle && employee.empType !== "Sub Contractors") {
        tempErrors.jobTitle = "Job Title is required";
        tempErrors.step = "1";
      }

      if (!employee.empType || employee.empType === "select") {
        tempErrors.empType = "Employee Type is required";
        tempErrors.step = "1";
      }

      if (
        (Object.keys(phoneError).length > 0 && !isPhoneErrorEmpty()) ||
        (employee.phoneNumbers.length > 0 &&
          employee.phoneNumbers[0].phoneNo === "")
      ) {
        tempErrors.phoneError = "Phone number is required";
        tempErrors.step = "1";
      }

      if (employee?.phoneNumbers.filter((e) => e.phoneNo === "").length > 0) {
        tempErrors.phoneError = "Phone number is required";
        tempErrors.step = "1";
      }

      if (!employee.address) {
        tempErrors.address = "Street 1 is required";
        tempErrors.step = "1";
      }

      if (!employee.city) {
        tempErrors.city = "City is required";
        tempErrors.step = "1";
      }

      if (!employee.stateProvince) {
        tempErrors.stateProvince = "State is required";
        tempErrors.step = "1";
      }

      if (!employee.postalCode) {
        tempErrors.postalCode = "Zip Code is required";
        tempErrors.step = "1";
      }

      if (isValidEmergencyPhone) {
        tempErrors.emergencyContactPhone = "Invalid Emergency phone number";
        tempErrors.step = "1";
      }
    }
    if (step === 1) {
      if (!employee.roleID || employee.roleID === "0") {
        tempErrors.roleID = "Employee Role is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (!employee.departmentID || employee.departmentID === "select") {
        tempErrors.departmentID = "Department is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (!employee.skillID) {
        tempErrors.skillID = "Skill Type is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        !employee.employmentStatus ||
        employee.employmentStatus === "select"
      ) {
        tempErrors.employmentStatus = "Employment Status is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        employee.probationEndDate !== "" &&
        employee.probationEndDate !== "0001-01-01T00:00:00" &&
        employee.hireDate !== "" &&
        employee.probationEndDate < employee.hireDate
      ) {
        tempErrors.probationEndDate =
          "Probation Date cannot be earlier than Hire Date";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        (!employee.wageType && employee.empType !== "Sub Contractors") ||
        (employee.wageType === "select" &&
          employee.empType !== "Sub Contractors")
      ) {
        tempErrors.wageType = "Payment Type is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        !employee.wageAmount &&
        employee.wageType === "Hourly" &&
        employee.wageAmount == 0
      ) {
        tempErrors.wageAmount = "Hourly Rate is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (!employee.wageAmount && employee.wageType === "Salary") {
        tempErrors.wageAmount = "Annual Salary is required";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (!isAlwaysAvailable && !getWorkingHours(employee)?.hoursFrom) {
        tempErrors.hoursFrom = "Set availabilty first.";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }
      if (!isAlwaysAvailable && !getWorkingHours(employee)?.hoursTo) {
        tempErrors.hoursTo = "Set availabilty first.";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        !isAlwaysAvailable &&
        getWorkingHours(employee)?.hoursFrom &&
        getWorkingHours(employee)?.hoursFrom >=
          getWorkingHours(employee)?.hoursTo
      ) {
        tempErrors.hoursFrom = "Start time must be earlier than end time.";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }

      if (
        !isAlwaysAvailable &&
        getWorkingHours(employee)?.hoursFrom &&
        getWorkingHours(employee)?.hoursFrom ===
          getWorkingHours(employee)?.hoursTo
      ) {
        tempErrors.hoursFrom =
          "Start time and end time cannot be the same or empty.";
        tempErrors.hoursTo =
          "Start time and end time cannot be the same or empty.";
        tempErrors.step = tempErrors.step === "1" ? "1" : "2";
      }
    }
    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  const handleNext = () => {
    const isValid = validateFields(activeStep);
    if (!isValid) return;

    if (activeStep === steps.length - 1) {
      handleSave();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const fetchData = async () => {
    try {
      let emp = await GetEmployees(0, navigate);
      setAllEmployees(emp);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  const GetDuplicates = () => {
    let DublicateEmployees = AllEmployees?.filter(
      (x) =>
        x.firstName === employee?.firstName && x.lastName === employee?.lastName
    );
    return DublicateEmployees;
  };

  // const fetchDepartments = async () => {
  //   try {
  //     let dep = await GetDepartments(navigate);
  //     setDepartments(dep);
  //   } catch (error) {
  //     console.error("Error fetching Departments data:", error);
  //   }
  // };

  const fetchSkills = async () => {
    try {
      let skillsList = await GetSkills(0, navigate);
      setSkills(skillsList);
    } catch (error) {
      console.error("Error fetching Skills data:", error);
    }
  };

  const handleDayChange = (Day, value, hoursFrom, hoursTo) => {
    setEmployee((prevState) => {
      const currentHours = prevState.employeeHours
        ? prevState.employeeHours
        : WeekDay;

      let fromHours = hoursFrom ? hoursFrom : "07:00:00";
      let toHours = hoursTo ? hoursTo : "21:00:00";

      const updatedWeekDays = currentHours?.map((day) =>
        day.dayOfWeek === Day
          ? {
              ...day,
              hoursFrom: value ? fromHours : null,
              hoursTo: value ? toHours : null,
            }
          : day
      );

      return { ...prevState, employeeHours: updatedWeekDays };
    });
  };

  const handleFromHoursChange = (hoursFrom) => {
    setEmployee((prevState) => {
      const currentHours = prevState.employeeHours
        ? prevState.employeeHours
        : WeekDay;

      const updatedWeekDays = currentHours?.map((day) =>
        day.hoursFrom !== null ? { ...day, hoursFrom: hoursFrom } : day
      );

      return { ...prevState, employeeHours: updatedWeekDays };
    });
  };

  const handleToHoursChange = (hoursTo) => {
    setEmployee((prevState) => {
      const currentHours = prevState.employeeHours
        ? prevState.employeeHours
        : WeekDay;

      const updatedWeekDays = currentHours?.map((day) =>
        day.hoursTo !== null ? { ...day, hoursTo: hoursTo } : day
      );

      return { ...prevState, employeeHours: updatedWeekDays };
    });
  };

  const handleDeleteTag = (tagName) => {
    setEmployee((prevEmployee) => ({
      ...prevEmployee,
      tags: prevEmployee.tags.filter((tag) => tag.tagName !== tagName),
    }));
  };

  const handleTagEntered = (tagName) => {
    if (tagName == "") return false;

    const lowerCaseTagName = tagName.toLowerCase();

    setEmployee((prevEmployee) => {
      const tagExists = prevEmployee.tags?.some(
        (tag) => tag.tagName.toLowerCase() === lowerCaseTagName
      );

      if (tagExists) {
        return prevEmployee;
      }
      const updatedEmployee = {
        ...prevEmployee,
        tags: [...(prevEmployee.tags || []), { tagName }],
      };
      return updatedEmployee;
    });
    document.getElementById("TxtTag").value = "";
  };

  const validateIndiviual = (field, value) => {
    const fieldInfo = mandatoryFields.find((f) => f.key === field);
    let displayName = fieldInfo?.displayName || field;

    if (employee.wageType === "Salary" && field === "wageType") {
      displayName = "Annual Salary";
    }

    if (employee.wageType === "Hourly" && field === "wageType") {
      displayName = "Hourly Rate";
    }

    if (value) {
      setErrors((prevState) => ({ ...prevState, [field]: "" }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        [field]: `${displayName} is required.`,
      }));
    }
  };

  const handleChange = (field, value) => {
    const nameRegex = /^[A-Za-z]+$/;

    if (field === "firstName" || field === "lastName") {
      if (value && !nameRegex.test(value)) {
        return;
      }
    }

    if (field === "wageType") {
      setEmployee((prevState) => ({
        ...prevState,
        [field]: value,
        ["wageAmount"]: 0,
      }));
      errors.wageAmount = "";
    } else if (field === "empType") {
      if (value === "Sub Contractors") {
        setEmployee((prevState) => ({
          ...prevState,
          [field]: value,
          roleID: "7",
          employmentStatus: "Contract",
        }));
        errors.roleID = "";
      } else {
        setEmployee((prevState) => ({
          ...prevState,
          [field]: value,
          roleID: null,
          employmentStatus: null,
        }));
      }
    } else if (field === "postalCode") {
  const zipCodeRegex = /^\d{0,5}$/;
  if (zipCodeRegex.test(value)) {
    setEmployee((prevState) => ({ ...prevState, [field]: value }));

    // Prefill county but still allow editing
    getCounties(value).then(({ county }) => {
      if (county) {
        setEmployee((prevState) => ({
          ...prevState,
           county: county // Only prefill if it's empty
        }));
      }
    });
  }
    } else {
      setEmployee((prevState) => ({ ...prevState, [field]: value }));
    }
    if (mandatoryFields.filter((f) => f.key === field).length > 0) {
      validateIndiviual(field, value);
    }
  };

  const handleEmergencyPhoneChange = (value, data) => {
    setEmployee((prevState) => ({
      ...prevState,
      ["emergencyContactPhone"]: value,
    }));
    handleIsValidPhone(employee.emergencyContactPhone, data);
  };

  const getWorkingHours = (employee) => {
    const validHours = employee?.employeeHours?.find(
      (day) =>
        day.hoursFrom &&
        day.hoursTo &&
        day.hoursFrom.ticks !== 0 &&
        day.hoursTo.ticks !== 0
    );

    if (validHours) {
      return {
        hoursFrom: validHours.hoursFrom,
        hoursTo: validHours.hoursTo,
      };
    }

    // Return null if no valid hours are found
    return null;
  };

  const handleAddPhoneField = () => {
    const phoneRegex = /^\d{10}$/;
    setEmployee((prev) => {
      const allPhoneNumbersValid = prev.phoneNumbers?.every((phone, index) => {
        const IsValidPhone =
          phone?.phoneNo?.trim() !== "" &&
          phone?.phoneNo?.trim() !== "1" &&
          phoneRegex.test(phone.phoneNo);
        if (!IsValidPhone) {
          setPhoneError((prevErrors) => ({
            ...prevErrors,
            [index]: "Please enter a valid phone number before adding another.",
          }));
        } else {
          setPhoneError((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
          });
        }

        return IsValidPhone;
      });

      if (allPhoneNumbersValid) {
        return {
          ...prev,
          phoneNumbers: [
            ...prev.phoneNumbers,
            { phoneNo: "", isPrimary: false },
          ],
        };
      } else {
        return prev;
      }
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isEmployeeAvailable = (employeeHours) => {
    return employeeHours?.some(
      (day) => day.hoursFrom !== null && day.hoursTo !== null
    );
  };

  const handleIsValidPhone = (value, country) => {
    if (value && country) {
      const dialCode = country.dialCode;
      if (value.length < 10 || value.slice(0, dialCode.length) !== dialCode) {
        setIsValidEmergencyPhone(false);
      } else {
        setIsValidEmergencyPhone(true);
      }
    } else {
      setIsValidEmergencyPhone(false);
    }
  };

  let isPhoneErrorEmpty = () => {
    let isAllEmpty = false;
    for (var i = 0; i < Object.keys(phoneError).length; i++) {
      if (Object.values(phoneError)[i] === "") isAllEmpty = true;
      return isAllEmpty;
    }
  };

  const PhoneResolve = () => {
    for (var i = 0; i < employee?.phoneNumbers?.length; i++) {
      handleValidation(i);
    }
  };

  const handleAlwaysAvailable = (e) => {
    setIsAlwaysAvailable(e);
    if (getWorkingHours() === null && e) {
      setEmployee((prev) => ({
        ...prev,
        employeeHours: AlwaysAvailableWeekDay,
      }));
    } else {
      setEmployee((prev) => ({
        ...prev,
        employeeHours: WeekDay,
      }));
    }
  };

  const handleStateInputChange = (field, value) => {
  const expanded = expandStateName(value);
  handleChange(field, expanded);
};

  const handleValidation = (index) => {
    if (employee?.phoneNumbers[index]?.phoneNo === "") {
      setIsValidPhone((prev) => ({ ...prev, [index]: false }));
    } else {
      setIsValidPhone((prev) => ({ ...prev, [index]: true }));
    }
  };

  const handlePhoneFieldChange = (index, value) => {
    const phoneRegex = /^\d{10}$/;

    const isDuplicate =
      employee &&
      Array.isArray(employee.phoneNumbers) &&
      employee.phoneNumbers.some(
        (phoneNumber, i) =>
          phoneNumber &&
          phoneNumber.phoneNo &&
          i !== index &&
          phoneNumber.phoneNo === value &&
          value !== "" &&
          value.length >= 10
      );

    const isValidFormat = phoneRegex.test(value);

    if (isDuplicate) {
      setPhoneError((prev) => ({
        ...prev,
        [index]: "Phone number already exists.",
      }));
      setIsValidPhone((prev) => ({
        ...prev,
        [index]: false,
      }));
    } else if (!isValidFormat && value !== "" && value !== "1") {
      setPhoneError((prev) => ({
        ...prev,
        [index]: "Invalid phone number.",
      }));
      setIsValidPhone((prev) => ({
        ...prev,
        [index]: false,
      }));
    } else if (value === "1" || value === "") {
      setPhoneError((prev) => ({
        ...prev,
        [index]: "Phone number is required.",
      }));
      setIsValidPhone((prev) => ({
        ...prev,
        [index]: false,
      }));
    } else {
      setPhoneError((prev) => ({
        ...prev,
        [index]: "",
      }));
      setIsValidPhone((prev) => ({
        ...prev,
        [index]: true,
      }));
    }

    const updatedPhoneNumbers = employee.phoneNumbers.map((phoneNumber, i) => {
      if (
        i === index &&
        index === 0 &&
        employee.phoneNumbers.filter((x) => x.isPrimary === true).length === 0
      ) {
        return { ...phoneNumber, phoneNo: value, isPrimary: true };
      } else if (i === index) {
        return { ...phoneNumber, phoneNo: value };
      } else return phoneNumber;
    });
    setEmployee((prev) => ({
      ...prev,
      phoneNumbers: updatedPhoneNumbers,
    }));
  };

  const handleDeletePhoneField = (index) => {
    const updatedPhoneNumbers = employee.phoneNumbers.filter(
      (_, i) => i !== index
    );
    setEmployee((prev) => ({
      ...prev,
      phoneNumbers: updatedPhoneNumbers,
    }));
    setPhoneError((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const handleSetPrimary = (index) => {
    const isDuplicate = employee.phoneNumbers.some(
      (phoneNumber, i) =>
        i !== index &&
        phoneNumber.phoneNo === employee.phoneNumbers[index].phoneNo
    );
    if (!isDuplicate) {
      const updatedPhoneNumbers = employee.phoneNumbers.map(
        (phoneNumber, i) => {
          return { ...phoneNumber, isPrimary: i === index };
        }
      );

      setEmployee((prev) => ({
        ...prev,
        phoneNumbers: updatedPhoneNumbers,
      }));
    }
  };

  const handleSave = async () => {
    try {
      let DubEmployees = await GetDuplicates();
      if (DubEmployees.length > 0) {
        let List = getDubContent(DubEmployees);
        MySwal.fire({
      title: '<strong>Are you sure?</strong>',
      html: List,
      showCancelButton: true,
      confirmButtonText: 'Save Employee',
      cancelButtonText: 'Cancel',
      width: '420px', // Explicit width control
      padding: '1rem',
      focusConfirm: false,
      showCloseButton: true,
      reverseButtons: true,
      closeButtonHtml: '&times;', // This is the × symbol
      customClass: {
        popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
      }
    }).then((result) => {
          if (result.isConfirmed) {
            setButtonLoading(true);
            SaveEmp();
          }
        });
      } else {
        setButtonLoading(true);
        SaveEmp();
      }
    } catch (error) {
      console.error(error);
    }
  };

 const getDubContent = (dubEmp) => {
  let content = `One or more employees named "${employee?.firstName} ${employee?.lastName}" already exist:</br></br>`;

  content += `
    <style>  
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      th, td {
        text-align: center;
        border: 1px solid #ddd;
        padding: 5px 8px;
        padding-left: 20px; /* Adds left padding */
      }
      th {
        background-color: #f4f4f4;
      }
      tr:nth-child(even) {
        background-color: #f9f9f9;
      }
      tr:hover {
        background-color: #f1f1f1;
      }
      a.underline-link {
        text-decoration: underline;
        color:inherit;
        cursor: pointer;
      }
    </style> 
    <div style="overflow-y: scroll; max-height: 100px;">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
          </tr>
        </thead>
        <tbody>`;

  for (let i = 0; i < dubEmp.length; i++) {
    content += `
      <tr>
        <td>
          <a href="./detail/${dubEmp[i].employeeID}" target="_blank" class="underline-link">
            ${dubEmp[i].employeeID}
          </a>
        </td>
        <td>${dubEmp[i].firstName} ${dubEmp[i].lastName}</td>
      </tr>`;
  }

  content += `
        </tbody>
      </table>
    </div>`;

  return content;
};

  const fetchDepartments = async () => {
  try {
    const response = await getDepartments(navigate);
    
    if (!response.isError) {
      
      setDepartments(response.result);
    } else {
      toast.error(response.message || "Failed to fetch departments", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  } catch (error) {
    toast.error("An error occurred while fetching departments", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  }
};
const fetchJobTitles = async () => {
  try {
    const response = await getJobTitles(navigate);
    
    if (!response.isError) {
      setJobTitles(response.result || []);
    } else {
      toast.error(response.message || "Failed to fetch job titles", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  } catch (error) {
    toast.error("An error occurred while fetching job titles", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  }
};
const empStatusOptions = employee?.empType === "Sub Contractors"
  ? [
      { label: "Contract", value: "Contract" }
    ]
  : [
      { label: "Contract", value: "Contract" },
      { label: "Full Time", value: "FullTime" },
      { label: "Part Time", value: "PartTime" }
    ];


 const handleDepartmentCreated = (newDepartment) => {
  fetchDepartments();
  if (newDepartment && newDepartment.departmentID) {
    handleChange("departmentID", newDepartment.departmentID);
  }
};
 
  const departmentProps = {
    existingDepartment: null,
    fetchDepartments: fetchDepartments,
    departmentContent: {
      title: "Create New Department",
      entity: "create"
    },
    onDepartmentCreated: handleDepartmentCreated
  };

  const handleJobTitleCreated = (newJobTitle) => {
  fetchJobTitles();
  if (newJobTitle && newJobTitle.titleName) {
    handleChange("jobTitle", newJobTitle.titleName);
  }
 };

  const jobTitleProps = {
    existingJobTitle: null,
    fetchJobTitles: fetchJobTitles,
    jobTitleContent: {
      title: "Create New Job Title",
      entity: "create"
    },
    onJobTitleCreated: handleJobTitleCreated
  };
  const SaveEmp = async () => {
    try {
      const result = await SaveEmployee(employee);
      if (result && !result.isError) {
        setEmployee(initialdata);
        toast.success(`Created! Employee has been successfully created.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        handleClose();
        setActiveStep(0);
        fetchEmployees();
      } else {
        toast.error(`Failed! ${result.result[0].failureReason}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error saving employee:", error);
    } finally {
      setButtonLoading(false);
    }
  };
  //#endregion Handlers

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

   const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';

  const str = value.toString();
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    const num = Number(parts[0]);
    if (!isNaN(num)) {
      parts[0] = num.toLocaleString('en-US');
    }
  }

  return parts.join('.');
};

const removeCommas = (value) => value.replace(/,/g, '');

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ 
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: isSmallScreen ? 4 : 0
          }}>
            {/* Left Column */}
            <Box sx={{
              width: "100%",
              maxWidth: isSmallScreen ? "100%" : "50%",
              pr: isSmallScreen ? 0 : 6
            }}>
              <Grid container spacing={isSmallScreen ? 3 : 5}>
                {/* Employee Type */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel
                      required
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                        "& .MuiFormLabel-asterisk": { color: "error.main" },
                        marginBottom: "4px",
                      }}
                    >
                      Employee Type
                    </FormLabel>
                    <RadioGroup
                      row={!isSmallScreen}
                      aria-labelledby="employeeTypeLabel"
                      name="employeeTypeSelection"
                      value={employee?.empType ? employee?.empType : "Employees"}
                      onChange={(e) => handleChange("empType", e.target.value)}
                    >
                      <FormControlLabel
                        value="Employees"
                        control={<Radio color="success" />}
                        label="Employee"
                      />
                      <FormControlLabel
                        value="Sub Contractors"
                        control={<Radio color="success" />}
                        label="Subcontractor"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                {/* Name Fields */}
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label={"First Name"}
                    required
                    placeholder="First Name"
                    value={employee.firstName || ""}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Last Name"
                    required
                    placeholder="Last Name"
                    value={employee?.lastName || ""}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </Grid>
                
                {/* Email and Job Title */}
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Email"
                    required
                    placeholder="name@example.com"
                    value={employee.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
             <Grid item xs={12} sm={6}>
                <CustomAutocomplete
                  options={[
                    { key: "create_new", value: "+ Create New Job Title", isCreateOption: true },
                    { key: "select", value: "Select", disabled: true },
                    ...(
                      (jobTitles?.map(job => {
                        const jobtitleID = job.jobtitleID || job.jobTitleID || job.id;
                        const titleName = job.titleName || job.jobTitle || job.name;
                        
                        if (jobtitleID && titleName) {
                          return { 
                            key: String(jobtitleID), 
                            value: titleName 
                          };
                        } else {
                          console.warn("Invalid job title object:", job);
                          return null;
                        }
                      }) || [])
                      .filter(Boolean)
                      .sort((a, b) => a.value.localeCompare(b.value))
                    )
                  ]}
                  getOptionLabel={(opt) => opt.value || ""}
                  isOptionEqualToValue={(opt, val) => opt.key === val.key}
                  value={
                    jobTitles?.map(j => ({
                      key: String(j.jobtitleID || j.jobTitleID || j.id),
                      value: j.titleName || j.jobTitle || j.name
                    }))?.find(opt => opt.value === employee.jobTitle) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue?.isCreateOption) {
                      setJobTitleDialogOpen(true);
                    } else if (newValue && newValue.key !== "select") {
                      handleChange("jobTitle", newValue.value);
                    } else {
                      handleChange("jobTitle", "");
                    }
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Job Title"
                      required={employee?.empType !== "Sub Contractors"}
                      placeholder={jobTitles?.length === 0 ? "No job titles available" : "Select"}
                      error={!!errors.jobTitle}
                      helperText={errors.jobTitle}
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.isCreateOption ? (
                        <Typography fontWeight="bold" component="span">
                          {option.value}
                        </Typography>
                      ) : (
                        option.value
                      )}
                    </li>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options.filter(option => !option.disabled);

                    return options.filter(option =>
                      !option.disabled &&
                      (option.isCreateOption || option.value.toLowerCase().includes(inputValue.toLowerCase()))
                    );
                  }}
                  PaperProps={{
                    sx: {
                      maxHeight: 200
                    }
                  }}
                />
              </Grid>
                
                {/* Referred By and DOB */}
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Referred By"
                    placeholder="Referred By"
                    value={employee?.referredBy || ""}
                    onChange={(e) => handleChange("referredBy", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      marginBottom: "4px",
                      color: "text.primary",
                      height: "100%",
                    }}
                  >
                    Date of Birth
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        employee?.dateOfBirth
                          ? dayjs(employee.dateOfBirth)
                          : null
                      }
                      onChange={(newValue) => {
                        handleChange("dateOfBirth", newValue);
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      disableFuture
                      sx={{ width: "100%" }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                {/* Phone Numbers */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormLabel
                      required
                      sx={{
                        display: "flex",
                        fontSize: "13px",
                        color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                        "& .MuiFormLabel-asterisk": { color: "error.main" },
                        marginBottom: "4px",
                      }}
                    >
                      Phone
                    </FormLabel>
                    
                    {employee.phoneNumbers?.map((field, index) => (
                      <Box
                        key={field.phoneID || index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                          flexDirection: isSmallScreen ? 'column' : 'row'
                        }}
                      >
                        <Box sx={{ 
                          width: isSmallScreen ? '100%' : '70%',
                          mb: isSmallScreen ? 1 : 0,
                          display: "flex",
                          flexDirection: "column",
                        }}>
                          <PhoneInput
                            placeholder="Phone Number"
                            country={"us"}
                            onlyCountries={["us"]}
                            disableDropdown={true}
                            regions={"america"}
                            disableCountryCode={true}
                            required
                            label={`Phone # ${index + 1}`}
                            isValid={isValidPhone[index]}
                            copyNumbersOnly={true}
                            jumpCursorToEnd={false}
                            value={field.phoneNo || ""}
                            inputClass="custom-phone-input"
                            onChange={(e) => handlePhoneFieldChange(index, e)}
                            containerStyle={{
                              width: "100%",
                              margin: 0,
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "39px",
                              border: !isValidPhone[index]
                                ? "1px solid #EA5455"
                                : "1px solid rgba(208, 212, 241, 0.2)",
                              borderRadius: "6px",
                              paddingLeft: "48px",
                              backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                              color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                            }}
                          />
                          {phoneError[index] && (
                            <FormHelperText
                              sx={{
                                color: "#EA5455",
                                mt: "2px",
                                mb: "6px",
                                lineHeight: 1.154,
                                fontSize: "0.8125rem",
                                fontFamily:
                                  'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                              }}
                            >
                              {phoneError[index]}
                            </FormHelperText>
                          )}
                          {!phoneError[index] &&
                            errors.phoneError &&
                            employee?.phoneNumbers[index].phoneNo === "" && (
                              <FormHelperText
                                sx={{
                                  color: "#EA5455",
                                  mt: "2px",
                                  mb: "6px",
                                  lineHeight: 1.154,
                                  fontSize: "0.8125rem",
                                  fontWeight: 400,
                                  fontFamily:
                                    'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                                }}
                              >
                                {errors.phoneError}
                              </FormHelperText>
                            )}
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: isSmallScreen ? 'flex-end' : 'center',
                          width: isSmallScreen ? '100%' : '30%',
                          gap: 1
                        }}>
                          <IconButton
                            title="Mark as Primary"
                            color="secondary"
                            onClick={() => handleSetPrimary(index)}
                            disabled={field.isPrimary}
                            size={isSmallScreen ? "small" : "medium"}
                          >
                            {field.isPrimary ? (
                              <StarIcon sx={{ color: "#E7D557" }} />
                            ) : (
                              <StarOutlineIcon />
                            )}
                          </IconButton>
                          <IconButton
                            title="Delete"
                            color="secondary"
                            onClick={() => handleDeletePhoneField(index)}
                            disabled={field.isPrimary}
                            size={isSmallScreen ? "small" : "medium"}
                          >
                            <Icon icon="tabler:trash" />
                          </IconButton>
                        </Box>
                        
                        
                      </Box>
                    ))}
                    
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleAddPhoneField}
                      sx={{ 
                        width: isSmallScreen ? '50%' : 'auto',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start'
                      }}
                    >
                      Add Phone
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            {/* Right Column - Address and Emergency Contact */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: isSmallScreen ? "100%" : "50%",
                pl: isSmallScreen ? 0 : 6,
                mt: isSmallScreen ? 4 : 0,
                borderLeft: isSmallScreen ? "none" : "1px solid #E1E4EA",
                borderTop: isSmallScreen ? "1px solid #E1E4EA" : "none",
                pt: isSmallScreen ? 4 : 0
              }}
            >
              <Grid container spacing={isSmallScreen ? 2 : 3}>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color: "text.disabled",
                      "& .MuiDivider-wrapper": { px: isSmallScreen ? 2 : 6 },
                      fontSize: theme.typography.body2.fontSize,
                      my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                  >
                    Address
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    required
                    placeholder="Street 1"
                    // label="Street 1"                    
                    value={employee?.address || ""}
                    onChange={(e) => handleChange("address", e.target.value)}
                    error={!!errors.address}
                    helperText={errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <CustomTextField
                    fullWidth
                    placeholder="Street 2"
                    // label="Street 2"
                    value={employee?.street2 || ""}
                    onChange={(e) => handleChange("street2", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    placeholder="City"
                    // label="City"
                    required
                    value={employee?.city || ""}
                    onChange={(e) => handleChange("city", e.target.value)}
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomAutocomplete
                      open={autocompleteOpen}
                      onClose={() => setAutocompleteOpen(false)}
                      options={stateNames}
                      id="autocomplete-custom"
                      value={
                        stateNames.find((option) => option === employee?.stateProvince) || null
                      }
                      onInputChange={(event, newInputValue) => {
                        const expandedValue = expandStateName(newInputValue);
                        handleChange("stateProvince", expandedValue);
                      }}
                      onChange={(event, newValue) => {
                        handleChange("stateProvince", newValue);
                      }}
                      getOptionLabel={(option) => option || ""}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          fullWidth
                          required
                          placeholder="State"
                          onClick={() => setAutocompleteOpen(!autocompleteOpen)}
                        />
                      )}
                      sx={{
                        "& .MuiInputBase-root": {
                          ...(!!errors.stateProvince && { borderColor: "error.main" }),
                        },
                      }}
                    />
                    <FormHelperText sx={{ fontSize: "13px", color: "error.main" }}>
                      {errors.stateProvince}
                    </FormHelperText>
                  </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    placeholder="Zip code"
                    // label="Zip code"
                    required
                    value={employee?.postalCode || ""}
                    onChange={(e) => handleChange("postalCode", e.target.value)}
                    error={!!errors.postalCode}
                    helperText={errors.postalCode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    placeholder="County"
                    // label="County"
                    required
                    value={employee?.county || ""}
                    onChange={(e) => handleChange("county", e.target.value)}
                  />
                </Grid>
  
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color: "text.disabled",
                      "& .MuiDivider-wrapper": { px: isSmallScreen ? 2 : 6 },
                      fontSize: theme.typography.body2.fontSize,
                      my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                  >
                    Emergency Contact
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    placeholder="Full Name"
                    label="Full Name"
                    value={employee?.emergencyContactName || ""}
                    onChange={(e) =>
                      handleChange("emergencyContactName", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    placeholder="Relation"
                    label="Relation"
                    value={employee?.emergencyContactRelationship || ""}
                    onChange={(e) =>
                      handleChange(
                        "emergencyContactRelationship",
                        e.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      display: "flex",
                      fontSize: "13px",
                      marginBottom: "4px",
                      color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                      "& .MuiFormLabel-asterisk": {
                        color: "error.main",
                      },
                    }}
                  >
                    Phone
                  </FormLabel>
                  <PhoneInput
                    placeholder="Phone Number"
                    country={"us"}
                    onlyCountries={["us"]}
                    disableDropdown={true}
                    regions={"america"}
                    disableCountryCode={true}
                    value={employee?.emergencyContactPhone || ""}
                    onChange={handleEmergencyPhoneChange}
                    containerStyle={{
                      width: "100%",
                      margin: 0,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "39px",
                      borderColor: "rgba(208, 212, 241, 0.2)",
                      borderRadius: "4px",
                      paddingLeft: "48px",
                      backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                      color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                    }}
                    inputClass="custom-phone-input"
                    inputProps={{
                      name: "Emergency Contact",
                      required: false,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box
            sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", md: "50%" },
                pr: { xs: 0, md: 4 },
              }}
            >
              <Grid container spacing={{ xs: 3, sm: 5 }}>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color: "text.disabled",
                      "& .MuiDivider-wrapper": { px: { xs: 2, sm: 6 } },
                      fontSize: theme.typography.body2.fontSize,
                      my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                  >
                    General Information
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {employee?.empType === "Sub Contractors" ? (
                    <CustomTextField
                      label="Employee Role"
                      fullWidth
                      select
                      required
                      disabled
                      value={employee?.roleID ? employee?.roleID : "0"}
                    >
                      <MenuItem value="7">Subcontractor</MenuItem>
                    </CustomTextField>
                  ) : (
                    // <CustomTextField
                    //   label="Employee Role"
                    //   fullWidth
                    //   select
                    //   required
                    //   defaultValue="0"
                    //   value={employee?.roleID ? employee?.roleID : "0"}
                    //   onChange={(e) => handleChange("roleID", e.target.value)}
                    //   error={!!errors.roleID}
                    //   helperText={errors.roleID}
                    // >
                    //   <MenuItem value="0" disabled>Select</MenuItem>
                    //   {customRoles?.map((role) => (
                    //     <MenuItem key={role.roleID} value={role.roleID}>
                    //       {role.roleName}
                    //     </MenuItem>
                    //   ))}
                    // </CustomTextField>
                    <CustomAutocomplete
                     options={[
                      { roleID: -1, roleName: "+ Create Role" },
                      ...[...customRoles].sort((a, b) =>
                        a.roleName.localeCompare(b.roleName)
                      ),
                    ]}
                      value={
                        customRoles.find((r) => r.roleID === employee.roleID) ||
                        null
                      }
                      getOptionLabel={(opt) => opt.roleName}
                      isOptionEqualToValue={(opt, val) =>
                        opt.roleID === val.roleID
                      }
                    onChange={(_, selected) => {
                      if (!selected) {
                        // Clear role
                        handleChange("roleID", null);
                        return;
                      }

                      if (selected.roleID === -1) {
                        handleOpenRoleDlg();
                      } else {
                        handleChange("roleID", selected.roleID);
                      }
                    }}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          fullWidth
                          label="Employee Role"
                          required
                          placeholder="Select or create role"
                          error={!!errors.roleID}
                          helperText={errors.roleID}
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* <CustomAutocomplete
                    open={departmentOpen}
                    onClose={() => setDepartmentOpen(false)}
                    options={departments.map((dept) => dept.value)}
                    id="autocomplete-custom"
                    onChange={(event, value) => {
                      const selectedDepartment = departments.find(
                        (dept) => dept.value === value
                      );
                      handleChange(
                        "departmentID",
                        selectedDepartment ? selectedDepartment.key : 0
                      );
                    }}
                    getOptionLabel={(option) => option || ""}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        fullWidth
                        required
                        label="Department"
                        placeholder="Select"
                        defaultValue={
                          employee?.departmentID
                            ? departments.find(
                                (dept) => dept.key === employee.departmentID
                              )?.value || ""
                            : ""
                        }
                        onClick={() => setDepartmentOpen(!departmentOpen)}
                        error={!!errors.departmentID}
                        helperText={errors.departmentID}
                      />
                    )}
                  /> */}
               <CustomAutocomplete
              options={[
                { key: "create_new", value: "+ Create New Department", isCreateOption: true },
                { key: "select", value: "Select", disabled: true },
                ...(
                  departments
                    ?.map(dept => {
                      const key = dept.departmentID || dept.key;
                      const value = dept.department || dept.value;
                      return key ? { key, value } : null;
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.value.localeCompare(b.value))
                  || []
                )
              ]}
              getOptionLabel={(opt) => opt.value || ""}
              isOptionEqualToValue={(opt, val) => opt.key === val.key}
              value={
                departments
                  ?.map(d => ({
                    key: d.departmentID,
                    value: d.department
                  }))
                  ?.sort((a, b) => a.value.localeCompare(b.value))   
                  ?.find(opt => opt.key == employee.departmentID) || null
              }
              onChange={(event, newValue) => {
                if (newValue?.isCreateOption) {
                  setDepartmentDialogOpen(true);
                } else if (newValue && newValue.key !== "select") {
                  handleChange("departmentID", newValue.key);
                } else {
                  handleChange("departmentID", "");
                }
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Department"
                  required
                  placeholder={departments?.length === 0 ? "No departments available" : "Select"}
                  error={!!errors.departmentID}
                  helperText={errors.departmentID}
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>{option.value}</li>
              )}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options.filter(option => !option.disabled);

                return options.filter(option =>
                  !option.disabled &&
                  (option.isCreateOption || option.value.toLowerCase().includes(inputValue.toLowerCase()))
                );
              }}
              PaperProps={{
                sx: {
                  maxHeight: 200
                }
              }}
            />
                </Grid>
              <Grid item xs={12} sm={6}>
            <CustomAutocomplete
              options={empStatusOptions}
              value={
                empStatusOptions.find(
                  (opt) => opt.value === employee?.employmentStatus
                ) || null
              }
              onChange={(e, newValue) =>
                handleChange("employmentStatus", newValue?.value || "")
              }
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Employment Status"
                  required
                  error={!!errors.employmentStatus}
                  helperText={errors.employmentStatus}
                  placeholder="Select"
                  size="small"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              PaperProps={{ sx: { maxHeight: 200 } }}
            />
          </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomAutocomplete
                    options={[
                      { id: -1, skillName: "Create Skill Type" },
                      ...[...(skillsList || [])].sort((a, b) =>
                        a.skillName.toLowerCase().localeCompare(b.skillName.toLowerCase())
                      ),
                    ]}
                    value={skillsList.find((s) => s.id === employee.skillID) || null}
                    onChange={(e, val) => {
                      if (val?.id === -1) {
                        setSkillTypeOpen(false); 
                        handleOpenSkillTypeDlg(); 
                        return;
                      }
                      handleChange("skillID", val?.id || null);
                    }}
                    getOptionLabel={(opt) => opt?.skillName || ""}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.id === -1 ? (
                          <Box display="flex" alignItems="center">
                            <AddIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography>Create Skill Type</Typography>
                          </Box>
                        ) : (
                          <Typography>{option.skillName}</Typography>
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Skill Type"
                        placeholder="Select Skill"
                        error={!!errors.skillID}
                        helperText={errors.skillID}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormLabel
                      sx={{
                        fontWeight: 400,
                        fontSize: "13px",
                        marginBottom: "4px",
                        color: "text.primary",
                        height: "100%",
                      }}
                    >
                      Hire Date
                    </FormLabel>
                    <DatePicker
                      sx={{ width: "100%" }}
                      value={
                        employee?.hireDate ? dayjs(employee.hireDate) : null
                      }
                      onChange={(newValue) => {
                        handleChange("hireDate", newValue);
                      }}
                      slotProps={{ textField: { size: "small" } }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      marginBottom: "4px",
                      color: "text.primary",
                      height: "100%",
                    }}
                  >
                    Probation Date
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      sx={{
                        width: "100%",
                        border: errors.probationEndDate
                          ? "1px solid #EA5455"
                          : "none",
                        borderRadius: errors.probationEndDate ? "6px" : "none",
                      }}
                      value={
                        employee?.probationEndDate
                          ? dayjs(employee.probationEndDate)
                          : null
                      }
                      onChange={(newValue) => {
                        handleChange("probationEndDate", newValue);
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      error={!!errors.probationEndDate}
                      helperText={errors.probationEndDate}
                    />
                  </LocalizationProvider>
                  {!!errors.probationEndDate && (
                    <FormHelperText
                      sx={{
                        color: "#EA5455",
                        mt: "2px",
                        mb: "6px",
                        lineHeight: 1.154,
                        fontSize: "0.8125rem",
                        fontFamily:
                          'Lexend, Inter, Public Sans, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                      }}
                    >
                      {errors.probationEndDate}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      marginBottom: "4px",
                      color: "text.primary",
                      height: "100%",
                    }}
                  >
                    Driver's License Expiration
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        employee?.drivingLicenseExpiryDate
                          ? dayjs(employee.drivingLicenseExpiryDate)
                          : null
                      }
                      onChange={(newValue) => {
                        handleChange("drivingLicenseExpiryDate", newValue);
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      sx={{ width: "100%" }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color: "text.disabled",
                      "& .MuiDivider-wrapper": { px: { xs: 2, sm: 6 } },
                      fontSize: theme.typography.body2.fontSize,
                      my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                  >
                    Employee Wage Details
                  </Divider>
                </Grid>

                {employee?.empType !== "Sub Contractors" ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Wage Type"
                        required
                        type="date"
                        defaultValue={employee?.wageType || "select"}
                        onChange={(e) =>
                          handleChange("wageType", e.target.value)
                        }
                        error={!!errors.wageType}
                        helperText={errors.wageType}
                      >
                        <MenuItem value="select" disabled>
                          Select
                        </MenuItem>
                        <MenuItem value="Hourly">Hourly</MenuItem>
                        <MenuItem value="Salary">Salary</MenuItem>
                      </CustomTextField>
                    </Grid>

                    {employee?.wageType === "Salary" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            required
                            label="Annual Salary"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  sx={{ fontSize: "1.32rem" }}
                                >
                                  $
                                </InputAdornment>
                              ),
                            }}
                            value={formatNumberWithCommas(employee?.wageAmount)}
                            onChange={(e) => {
                              const rawValue = removeCommas(e.target.value);
                              handleChange("wageAmount", rawValue);
                            }}
                            error={!!errors.wageAmount}
                            helperText={errors.wageAmount}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            label="Weekly Hours"
                            type="number"
                            value={employee?.weeklyHours ?? ""}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (input === "") {
                                handleChange("weeklyHours", "");
                                return;
                              }
                              const numValue = Number(input);
                              if (!isNaN(numValue)) {
                                const clampedValue = Math.min(
                                  Math.max(numValue, 0),
                                  168
                                );
                                handleChange("weeklyHours", clampedValue);
                              }
                            }}
                            onBlur={(e) => {
                              if (
                                e.target.value === "" ||
                                isNaN(Number(e.target.value))
                              ) {
                                handleChange("weeklyHours", 40);
                              }
                            }}
                            inputProps={{
                              max: 168,
                              min: 0,
                              step: 1,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            disabled
                            label="Hourly Rate"
                            value={
                              employee?.wageAmount
                                ? new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    parseFloat(
                                      employee.wageAmount /
                                        (employee?.weeklyHours
                                          ? employee?.weeklyHours * 52
                                          : 40 * 52)
                                    )
                                  )
                                : "$0.00"
                            }
                            onChange={(e) =>
                              handleChange("wageAmount", e.target.value)
                            }
                          />
                        </Grid>
                      </>
                    )}

                    {employee?.wageType === "Hourly" && (
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          type="text"
                          label="Hourly Rate"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{ fontSize: "1.32rem" }}
                              >
                                $
                              </InputAdornment>
                            ),
                          }}
                          required
                          error={!!errors.wageAmount}
                          helperText={errors.wageAmount}
                          value={formatNumberWithCommas(employee?.wageAmount)}
                          onChange={(e) => {
                            const rawValue = removeCommas(e.target.value);
                            handleChange("wageAmount", e.target.value, rawValue);
                          }}
                        />
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        select
                        label="Wage Type"
                        type="date"
                        defaultValue={employee?.wageType || "select"}
                        onChange={(e) =>
                          handleChange("wageType", e.target.value)
                        }
                        error={!!errors.wageType}
                        helperText={errors.wageType}
                      >
                        <MenuItem value="select">Select</MenuItem>
                        <MenuItem value="Hourly">Hourly</MenuItem>
                        <MenuItem value="Salary">Salary</MenuItem>
                        <MenuItem value="Per Day">Per Day</MenuItem>
                        <MenuItem value="Per Job">Per Job</MenuItem>
                        <MenuItem value="Per Visit">Per Visit</MenuItem>
                      </CustomTextField>
                    </Grid>
                    {employee?.wageType === "Salary" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            label="Annual Salary"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  sx={{ fontSize: "1.32rem" }}
                                >
                                  $
                                </InputAdornment>
                              ),
                            }}
                            value={formatNumberWithCommas(employee?.wageAmount)}
                            onChange={(e) => {
                              const rawValue = removeCommas(e.target.value);
                              handleChange("wageAmount", rawValue);
                            }}
                            error={!!errors.wageAmount}
                            helperText={errors.wageAmount}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            label="Weekly Hours"
                            type="number"
                            value={employee?.weeklyHours ?? ""}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (input === "") {
                                handleChange("weeklyHours", "");
                                return;
                              }
                              const numValue = Number(input);
                              if (!isNaN(numValue)) {
                                const clampedValue = Math.min(
                                  Math.max(numValue, 0),
                                  168
                                );
                                handleChange("weeklyHours", clampedValue);
                              }
                            }}
                            onBlur={(e) => {
                              if (
                                e.target.value === "" ||
                                isNaN(Number(e.target.value))
                              ) {
                                handleChange("weeklyHours", 40);
                              }
                            }}
                            inputProps={{
                              max: 168,
                              min: 0,
                              step: 1,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            disabled
                            label="Hourly Rate"
                            value={
                              employee?.wageAmount
                                ? new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    parseFloat(
                                      employee.wageAmount /
                                        (employee?.weeklyHours
                                          ? employee?.weeklyHours * 52
                                          : 40 * 52)
                                    )
                                  )
                                : "$0.00"
                            }
                            onChange={(e) =>
                              handleChange("wageAmount", e.target.value)
                            }
                          />
                        </Grid>
                      </>
                    )}
                    {employee?.wageType === "Hourly" && (
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          type="text"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{ fontSize: "1.32rem" }}
                              >
                                $
                              </InputAdornment>
                            ),
                          }}
                          label="Hourly Rate"
                          error={!!errors.wageAmount}
                          helperText={errors.wageAmount}
                          value={formatNumberWithCommas(employee?.wageAmount)}
                          onChange={(e) => {
                            const rawValue = removeCommas(e.target.value);
                            handleChange("wageAmount", e.target.value, rawValue);
                          }}
                        />
                      </Grid>
                    )}
                    {(employee?.wageType === "Per Day" || employee?.wageType === "Per Job" || employee?.wageType === "Per Visit") && (
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          type="text"
                          label={employee?.wageType === "Per Day" ? 'Day Rate' : employee?.wageType === "Per Job" ? 'Job Rate' : 'Visit Rate'}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{ fontSize: "1.32rem" }}
                              >
                                $
                              </InputAdornment>
                            ),
                          }}
                          error={!!errors.wageAmount}
                          helperText={errors.wageAmount ? 'Rate amount is required' : ''}
                          value={formatNumberWithCommas(employee?.wageAmount)}
                          onChange={(e) => {
                            const rawValue = removeCommas(e.target.value);
                            handleChange("wageAmount", e.target.value, rawValue);
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", md: "50%" },
                mt: { xs: 4, md: 0 },
                pl: { xs: 0, md: 4 },
                borderLeft: { xs: "none", md: "1px solid #E1E4EA" },
              }}
            >
              <Grid container spacing={{ xs: 3, sm: 5 }}>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      color: "text.disabled",
                      "& .MuiDivider-wrapper": { px: { xs: 2, sm: 6 } },
                      fontSize: theme.typography.body2.fontSize,
                      my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                  >
                    Employee Availability
                  </Divider>
                </Grid>
                
                 <Grid item xs={12}>
                  <FormControl>
                    <FormLabel id="clockInRequired">
                      Clock in/out required
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="clockInRequired"
                      name="clockInRequiredSelection"
                      value={employee.clockInOutRequired ? "yes" : "no"}
                      onChange={(e) =>
                        handleChange(
                          "clockInOutRequired",
                          e.target.value === "yes"
                        )
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio color="success" />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio color="success" />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    label="Always Available"
                    control={
                      <Checkbox
                        color="success"
                        checked={isAlwaysAvailable}
                        onChange={(e) =>
                          handleAlwaysAvailable(e.target.checked)
                        }
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {employee?.employeeHours
                      ? employee?.employeeHours?.map(
                          ({ dayOfWeek, hoursFrom, hoursTo }) => {
                            let isOff = hoursFrom !== null || isAlwaysAvailable;
                            return (
                              <FormControlLabel
                                // disabled={isAlwaysAvailable}
                                key={dayOfWeek}
                                label={
                                  <Typography>
                                    {dayOfWeek?.slice(0, 3)}
                                  </Typography>
                                }
                                control={
                                  <Checkbox
                                    color="success"
                                    // disabled={isAlwaysAvailable}
                                    checked={isOff}
                                    onChange={(e) =>
                                      handleDayChange(
                                        dayOfWeek,
                                        e.target.checked,
                                        getWorkingHours(employee)?.hoursFrom ||
                                          null,
                                        getWorkingHours(employee)?.hoursTo ||
                                          null
                                      )
                                    }
                                  />
                                }
                              />
                            );
                          }
                        )
                      : WeekDay.map(({ dayOfWeek, hoursFrom, hoursTo }) => {
                          let isOff = hoursFrom !== null || isAlwaysAvailable;
                          return (
                            <FormControlLabel
                              disabled={isAlwaysAvailable}
                              key={dayOfWeek}
                              label={
                                <Typography>
                                  {dayOfWeek?.slice(0, 3)}
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  color="success"
                                  disabled={isAlwaysAvailable}
                                  checked={isOff}
                                  onChange={(e) =>
                                    handleDayChange(
                                      dayOfWeek,
                                      e.target.checked,
                                      hoursFrom,
                                      hoursTo
                                    )
                                  }
                                />
                              }
                            />
                          );
                        })}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1">Working Hours</Typography>
                  {!isEmployeeAvailable(employee?.employeeHours) && (
                    <Typography variant="body2">
                      Please select at least one available day to set working
                      hours.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel
                    //required={!isAlwaysAvailable}
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      mb: 1,
                      color: "text.primary",
                      display: 'block'
                    }}
                  >
                    From
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={
                        isAlwaysAvailable
                          ? dayjs().hour(7).minute(0)
                          : getWorkingHours(employee)?.hoursFrom
                          ? dayjs(`2000-01-01T${getWorkingHours(employee).hoursFrom}`)
                          : null
                      }
                      onChange={(newTime) => {
                        if (newTime) {
                          const timeString = newTime.format('HH:mm:ss');
                          handleFromHoursChange(timeString);
                        }
                      }}
                      disabled={isAlwaysAvailable}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: isAlwaysAvailable ? false : !!errors.hoursFrom,
                          helperText: isAlwaysAvailable ? "" : errors.hoursFrom,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel
                    // required={!isAlwaysAvailable}
                    sx={{
                      fontWeight: 400,
                      fontSize: "13px",
                      mb: 1,
                      color: "text.primary",
                      display: 'block'
                    }}
                  >
                    To
                  </FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={
                        isAlwaysAvailable
                          ? dayjs().hour(23).minute(0)
                          : getWorkingHours(employee)?.hoursTo
                          ? dayjs(`2000-01-01T${getWorkingHours(employee).hoursTo}`)
                          : null
                      }
                      onChange={(newTime) => {
                        if (newTime) {
                          const timeString = newTime.format('HH:mm:ss');
                          handleToHoursChange(timeString);
                        }
                      }}
                      disabled={isAlwaysAvailable}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: isAlwaysAvailable ? false : !!errors.hoursTo,
                          helperText: isAlwaysAvailable ? "" : errors.hoursTo,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

               
              </Grid>
               <Grid item xs={12}>
                <Divider
                sx={{
                   color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 4 },
                     fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                     }}
                     >
                     Additional Details
                       </Divider>
                    </Grid>

              <Grid container spacing={{ xs: 3, sm: 5 }} sx={{ mt: 3 }}>
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    placeholder="Type a tag & hit Enter to add it to the list."
                    label="Tags"
                    id="TxtTag"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleTagEntered(e.target.value);
                      }
                    }}
                  />
                </Grid>

                {(employee?.tags || []).map((tag, index) => (
                  <Grid item xs="auto" key={index}>
                    <CustomChip
                      rounded
                      size="small"
                      skin="light"
                      sx={{
                        lineHeight: 1,
                        paddingTop: "13px",
                        paddingRight: "0 !important",
                        paddingBottom: "13px",
                        borderRadius: "12px !important",
                        fontSize: "medium",
                        border: "1px solid",
                      }}
                      color="info"
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "0 !important",
                          }}
                        >
                          {tag.tagName}
                          <IconButton
                            size="small"
                            sx={{ ml: 2 }}
                            onClick={() => handleDeleteTag(tag.tagName)}
                          >
                            <CloseIcon fontSize="x-small" />
                          </IconButton>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        );
      // case 2: {console.log(employee)
      //   // return (
      //   //   <AttachmentsSectionEmployee
      //   //     entity="Employee"
      //   //     entityID={employee.employeeID}
      //   //     userName={`${employee.firstName} ${employee.lastName}`}
      //   //     // onDiscardUploads={() => handleOpenUnsavedChangesDlg('upload')}
      //   //     employee={employee}
      //   //     toast={toast}
      //   //   />
      //   // );
      //   return (
      //     <Grid item xs={12}>
      //       <Box
      //         sx={{
      //           width: '100%',
      //           px: { xs: 2, md: 6 },     // same horizontal padding as your other panels
      //           py: { xs: 2, md: 4 },     // vertical padding
      //         }}
      //       >
      //         <AttachmentsSectionEmployee
      //           entity="Employee"
      //           entityID={employee.employeeID}
      //           userName={`${employee.firstName} ${employee.lastName}`}
      //           employee={employee}
      //           toast={toast}
      //         />
      //       </Box>
      //     </Grid>
      //   )
      // }
      default:
        return "Unknown Step";
    }
  };

  const memoizedSteps = useMemo(() => {
    return steps.map((step, index) => {
      const RenderAvatar = activeStep >= index ? CustomAvatar : Avatar;
      const isStepActive = activeStep === index;

      return (
        <CustomStep key={index} sx={{ paddingBottom: 0 }}>
          <StepLabel StepIconComponent={StepperCustomDot}>
            <div className="step-label">
              <RenderAvatar
                variant="rounded"
                {...(activeStep >= index && {
                  skin: "light",
                })}
                {...(isStepActive && { skin: "filled" })}
                {...(activeStep >= index && {
                  color: "success",
                })}
                sx={{
                  ...(isStepActive && {
                    boxShadow: (theme) => theme.shadows[3],
                    color: "#fff",
                  }),
                  ...(activeStep > index && {
                    color: (theme) => theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.48)' : hexToRGBA(theme.palette.primary.main, 0.4),
                  }),
                }}
              >
                <Icon icon={step.icon} />
              </RenderAvatar>
              <div>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  {...(activeStep === index && {
                    fontWeight: "550",
                    color: "text.primary",
                  })}
                >
                  {step.title}
                </Typography>
              </div>
            </div>
          </StepLabel>
        </CustomStep>
      );
    });
  }, [steps, activeStep]);
  
  const AddEmployeeProps = {
    // States
    activeStep,
    setActiveStep,
    value,
    setValue,
    btnLoading,
    setButtonLoading,
    phoneError,
    setPhoneError,
    autocompleteOpen,
    setAutocompleteOpen,
    isValidPhone,
    setIsValidPhone,
    AllEmployees,
    setAllEmployees,
    employee,
    setEmployee,
    departments,
    setDepartments,
    errors,
    setErrors,
    isAlwaysAvailable,
    setIsAlwaysAvailable,
    isValidEmergencyPhone,
    setIsValidEmergencyPhone,
    departmentOpen,
    setDepartmentOpen,
    jobTitleOpen,
    setJobTitleOpen,
    skillTypeOpen,
    setSkillTypeOpen,
    skills,
    setSkills,
    
    // Handlers
    handleNext,
    handleBack,
    handleDayChange,
    handleFromHoursChange,
    handleToHoursChange,
    handleDeleteTag,
    handleTagEntered,
    handleChange,
    handleEmergencyPhoneChange,
    handleAddPhoneField,
    handleDeletePhoneField,
    handleSetPrimary,
    handlePhoneFieldChange,
    handleAlwaysAvailable,
    handleSave,
    
    // Other values
    steps,
    memoizedSteps,
    getStepContent,
    initialdata,
    WeekDay,
    AlwaysAvailableWeekDay,
    mandatoryFields,
    phoneFields,
    theme,
    MySwal,
    SkillProps,
    navigate,
    resetForm,
    toast,
    RoleProps,
    departmentProps,
    departmentDialogOpen, 
    setDepartmentDialogOpen, 
    jobTitleDialogOpen, 
   setJobTitleDialogOpen,
   jobTitleProps,
  };

  return {
    AddEmployeeProps,
  };
};
